import React, { useEffect, useState } from 'react';

import usePathname from 'app-migration/hooks/usePathname';
import useRouter from 'app-migration/hooks/useRouter';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import { createUseStyles } from 'react-jss';

import SignUpComplianceAgree from 'components/Compliance/SignUpComplianceAgree';
import DebitCardSelect from 'components/DebitCardSelect';
import { DEBIT_DEPOSIT_AMOUNT, DEBIT_DEPOSIT_FEE } from 'constants/session';
import Button from 'DesignSystem2/Button/Button';
import { Icon } from 'DesignSystem2/Icon';
import { TextInput } from 'DesignSystem2/Input';
import SelectInput from 'DesignSystem2/Input/SelectInput';
import Pill from 'DesignSystem2/Pill/Pill';
import PillGroup from 'DesignSystem2/Pill/PillGroup';
import Text from 'DesignSystem2/Text';
import { Spacing } from 'DesignSystem2/Theme';
import Border from 'DesignSystem2/Theme/Border';
import Padding from 'DesignSystem2/Theme/Padding';
import Palette from 'DesignSystem2/Theme/Palette';
import Radius from 'DesignSystem2/Theme/Radius';
import { useStores } from 'hooks/use-stores';
import InputGroup from 'react_pages/SignUp/components/InputGroup';
import { FundMethods } from 'stores/ui/registration/registration';
import { toMoneyNoCents, toMoneyWithCentsIfCents } from 'utils/caseing';
import { setLocalStorageWithExpiration } from 'utils/storage';
import { openHelpCenterAccountId } from 'utils/support';
import { isPOBox } from 'utils/validation';

import states from '../../constants/states';
import BankAccountSelect from '../BankAccountSelect';
import DepositAmountInput from './DepositAmountInput';

const useStyles = createUseStyles({
    input: {
        borderRadius: Radius.x20,
        border: Border(Palette.Fill.x50),
    },
    buttonContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '16px',
    },
    emoji: {
        background: Palette.Brand.Primary,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
    },
    invalidMessage: {
        display: 'flex',
        gap: Spacing(1),
        alignItems: 'flex-start',
    },
    invalidBullet: {
        minWidth: '8px',
        minHeight: '8px',
        marginTop: '6px',
        background: Palette.Critical.x10,
        borderRadius: Radius.x10,
    },
});

interface DepositPrereviewStepProps {
    amount: number;
    fee?: number;
    bankId: string;
    setBankId: React.Dispatch<React.SetStateAction<string>>;
    cardId: string;
    setCardId: React.Dispatch<React.SetStateAction<string>>;
    review: () => void;
    isValidDeposit: (amount?: number) => boolean;
    setAmount: React.Dispatch<React.SetStateAction<number>>;
    invalidMessage: string | undefined;
    setInvalidMessage: React.Dispatch<React.SetStateAction<string | undefined>>;
    onNotNow?: (e: React.FormEvent) => void;
    setLoadingBanking?: (loading: boolean) => void;
}

const DepositPrereviewStep = ({
    amount,
    fee = 0,
    bankId,
    setBankId,
    cardId,
    setCardId,
    review,
    isValidDeposit,
    setAmount,
    invalidMessage,
    setInvalidMessage,
    onNotNow,
    setLoadingBanking,
}: DepositPrereviewStepProps): JSX.Element => {
    const classes = useStyles();
    const router = useRouter();
    const pathname = usePathname();

    const { uiStore, traderStore } = useStores();
    const userStore = traderStore.user;
    const registrationStore = uiStore.registration;
    const bankStore = traderStore.bank;
    const paymentsStore = traderStore.payments;
    const isDarkMode = uiStore.colorPalette.currentColorPalette === 'dark';

    const [stringAmount, setStringAmount] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);

    const [addressLineOne, setAddressLineOne] = useState<string>('');
    const [addressLineTwo, setAddressLineTwo] = useState<string>('');
    const [city, setCity] = useState<string>('');
    const [state, setState] = useState<string>('');
    const [zip, setZip] = useState<string>('');

    // Address validation states
    const [addressErrorMessage, setAddressErrorMessage] = useState('');
    const [invalidPostalCode, setInvalidPostalCode] = useState('');
    const [invalidState, setInvalidState] = useState<string>('');
    const [attemptedSubmit, setAttemptedSubmit] = useState(false);

    const dialog = uiStore.depositDialog;
    const isBankFundMethod = registrationStore.fundMethod === FundMethods.BANK;
    const isDebitCardFundMethod = registrationStore.fundMethod === FundMethods.DEBIT_CARD;

    const stateAbbreviations = states.map((stateLabelValue) => stateLabelValue.value);

    useEffect(() => {
        // Only record the metric if we're in the sign-up flow
        const isSignUp = pathname.includes('/sign-up');
        if (isSignUp) {
            userStore.metrics.recordSignUpMetric({
                step: 'depositToKalshi',
            });
        }
    }, []);

    // Reset attempted submit when changing steps
    useEffect(() => {
        if (dialog.step === 'billing-address') {
            setAttemptedSubmit(false);
        }
    }, [dialog.step]);

    useEffect(
        () => {
            if (isBankFundMethod) {
                bankStore.load(traderStore.user.getUserId, true);
                bankStore.loadDepositLimitInfo();
            }
        }, // TODO: check or fix hooks error
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isBankFundMethod],
    );

    useEffect(
        () => {
            if (amount !== 0) {
                setStringAmount(toMoneyWithCentsIfCents(amount));
            }
        }, // TODO: check or fix hooks error
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [amount],
    );

    const formatAmount = (amount: number, stringAmount?: string): void => {
        setInvalidMessage('');
        setAmount(amount);
        if (amount === 0) {
            setStringAmount('$0');
            return;
        }

        // If the string amount ends with a decimal part, keep it
        if (stringAmount && stringAmount.includes('.')) {
            const cents = stringAmount.split('.')[1];
            const centsLength = cents.length;
            if (centsLength < 2) {
                setStringAmount(`${toMoneyNoCents(amount)}.${cents}`);
                return;
            }
        }

        setStringAmount(toMoneyWithCentsIfCents(amount));
    };

    const clickPreset = (presetAmount: number): void => {
        const newAmount = amount + presetAmount;
        uiStore.heapTrack('ClickPreset', { amount: newAmount });
        formatAmount(newAmount);
        validateAmount(newAmount);
    };

    const returnToSelectTransferMethod = (): void => {
        setInvalidMessage('');
        setAmount(0);
        setLoading(false);
        dialog.setStep('prereview');
        if (setLoadingBanking) {
            setLoadingBanking(false);
        }
        registrationStore.setFundMethod(undefined);
    };

    const debitCardDepositValidation = (amountToValidate: number, isSubmitting: boolean = false): boolean => {
        // Check if user has completed document verification
        const documentVerificationResult = uiStore.registration?.kyc?.documentVerificationResult;
        const isDocVerified = documentVerificationResult === 'approved';

        // Get all relevant limits
        const generalDocVerificationLimit = bankStore.maxDepositAmountWithoutDocVerification;
        const debitCardDocVerificationLimit = bankStore.maxDebitCardDepositAmountWithoutDocVerification;
        const debitCardDailyLimit = (bankStore.maxDebitCardDepositAmountCents || 0) / 100;
        const dailyDepositLimit =
            bankStore.dailyDepositCentsAvailable !== undefined ? bankStore.dailyDepositCentsAvailable / 100 : undefined;

        // For unverified users, check doc verification limits
        if (!isDocVerified) {
            // Filter out undefined values but keep 0 values
            const docVerificationLimits = [generalDocVerificationLimit, debitCardDocVerificationLimit].filter(
                (limit) => limit !== undefined,
            );

            // Find the lowest limit (including 0)
            const lowestDocVerificationLimit =
                docVerificationLimits.length > 0 ? Math.min(...docVerificationLimits) : undefined;

            // Check if doc verification is required
            // If limit is 0, doc verification is always required
            // If limit > 0, check if amount exceeds it
            const requiresDocVerification =
                lowestDocVerificationLimit !== undefined &&
                (lowestDocVerificationLimit === 0 || amountToValidate > lowestDocVerificationLimit);

            if (requiresDocVerification) {
                // If submitting, redirect to doc verification
                if (isSubmitting) {
                    sessionStorage.setItem('pendingDebitDeposit', 'true');
                    sessionStorage.setItem('pendingDebitDepositAmount', amountToValidate.toString());
                    // Store card selection if it's a saved card
                    if (cardId && cardId !== 'add') {
                        sessionStorage.setItem('pendingDebitCardId', cardId);
                    }
                    dialog.close();
                    router.push('/sign-up/identity');
                    return false;
                }

                // If just validating and the daily limit is lower, show that error instead
                if (debitCardDailyLimit > 0 && amountToValidate > debitCardDailyLimit) {
                    setInvalidMessage(
                        `You can deposit up to ${toMoneyWithCentsIfCents(debitCardDailyLimit)} with debit card today`,
                    );
                    return false;
                }

                // Don't show any error (graceful transition to doc verification)
                setInvalidMessage('');
                return true;
            }
        }

        // Check against daily deposit limit across all rails (applies to all users)
        if (dailyDepositLimit !== undefined) {
            if (dailyDepositLimit === 0) {
                setInvalidMessage(`You've hit your daily deposit limit`);
                return false;
            }
            if (amountToValidate > dailyDepositLimit) {
                setInvalidMessage(
                    `You can deposit up to ${toMoneyWithCentsIfCents(dailyDepositLimit)} today across all methods`,
                );
                return false;
            }
        }

        // Check against debit card specific daily limit (applies to all users)
        if (debitCardDailyLimit === 0) {
            setInvalidMessage(`You've hit your limit for debit card today`);
            return false;
        }
        if (amountToValidate > debitCardDailyLimit) {
            setInvalidMessage(
                `You can deposit up to ${toMoneyWithCentsIfCents(debitCardDailyLimit)} with debit card today`,
            );
            return false;
        }

        // Ensure the minimum amount
        if (amountToValidate < 10) {
            setInvalidMessage('$10 minimum deposit');
            return false;
        }

        setInvalidMessage('');
        return true;
    };

    const validateAmount = (amountToValidate: number): void => {
        if (isBankFundMethod) {
            if (isValidDeposit(amountToValidate)) {
                setInvalidMessage('');
            }
        } else if (isDebitCardFundMethod) {
            debitCardDepositValidation(amountToValidate);
        }
    };

    const presetAmounts = isBankFundMethod ? [250, 1000, 5000] : [50, 75, 100];

    const pills = presetAmounts.map((value, i) => {
        return (
            <Pill
                key={i}
                text={`+${toMoneyWithCentsIfCents(value)}`}
                selected={false}
                className={'hover:bg-fill-x40'}
                onClick={(): void => {
                    clickPreset(value);
                }}
                buttonProps={{
                    style: {
                        transition: 'all 0.2s ease-in-out',
                        height: 48,
                        padding: `${Spacing(0.5)} ${Spacing(1.5)}`,
                        color: Palette.Green.x10,
                        backgroundColor: 'transparent',
                        border: Border(Palette.Fill.x40),
                        width: '100%',
                    },
                }}
            />
        );
    });

    if (dialog.step === 'kyc') {
        return (
            <div className={'flex flex-col mt-2 animate-fade-in'}>
                <div style={{ display: 'flex', gap: Spacing(2) }}>
                    <Icon.Check color={Palette.Brand.Primary} />
                    <Text.Body.x30>
                        {'As a federally regulated exchange, we collect your info to comply with U.S. laws.'}
                    </Text.Body.x30>
                </div>
                <Padding spacing={2} />
                <div style={{ display: 'flex', gap: Spacing(2) }}>
                    <Icon.Check color={Palette.Brand.Primary} />
                    <Text.Body.x30>
                        {'Your info is confidential, protected, and used solely for regulatory purposes.'}
                    </Text.Body.x30>
                </div>
                <Padding spacing={2} />
                <div style={{ display: 'flex', gap: Spacing(2) }}>
                    <Icon.Check color={Palette.Brand.Primary} />
                    <Text.Body.x30>
                        {'Added security for your money and account, preventing unauthorized access.'}
                    </Text.Body.x30>
                </div>
                <Padding spacing={3} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: Spacing(2) }}>
                    <Button
                        variant={'brand'}
                        text={'Continue'}
                        onClick={(e) => {
                            router.push('/sign-up/identity');
                            if (onNotNow) {
                                onNotNow(e);
                            }
                        }}
                    />
                    <Button
                        rank={'secondary'}
                        variant={'brand'}
                        text={'Learn more'}
                        onClick={(e) => {
                            e.preventDefault();
                            openHelpCenterAccountId(uiStore, 'KYCModal');
                        }}
                    />
                    <Button rank={'tertiary'} variant={'brand'} text={'Not now'} onClick={onNotNow} />
                </div>
            </div>
        );
    }

    // Validate billing address
    const validateBillingAddress = (): boolean => {
        let error = false;

        // Check required fields
        if (addressLineOne === '' || city === '' || state === '' || zip === '') {
            setInvalidMessage('Please fill out all required fields');
            error = true;
        }

        // Check if it's a P.O. Box
        if (isPOBox(addressLineOne)) {
            setAddressErrorMessage('Enter physical address, not a P.O. Box');
            error = true;
        } else {
            setAddressErrorMessage('');
        }

        // Validate state
        if (!stateAbbreviations.includes(state.toUpperCase())) {
            setInvalidState('Enter valid state abbreviation');
            error = true;
        } else {
            setInvalidState('');
        }

        // Postal Code Validation
        if (!/^\d{5}$/.test(zip)) {
            setInvalidPostalCode('Enter valid ZIP code');
            error = true;
        } else {
            setInvalidPostalCode('');
        }

        return !error;
    };

    const onContinueAddCard = (): void => {
        if (!debitCardDepositValidation(amount, true)) {
            return;
        }

        if (dialog.step === 'billing-address') {
            setAttemptedSubmit(true);
            if (!validateBillingAddress()) {
                return;
            }
        } else if (addressLineOne === '' || city === '' || state === '' || zip === '') {
            dialog.setStep('billing-address');
            // Don't set attemptedSubmit to true yet - we just entered the address step
            return;
        }

        // Store the amount and fee in the session storage
        if (amount > 0) {
            setLocalStorageWithExpiration(DEBIT_DEPOSIT_AMOUNT, amount);
        }
        if (fee > 0) {
            setLocalStorageWithExpiration(DEBIT_DEPOSIT_FEE, fee);
        }

        setLoading(true);
        paymentsStore.startPaymentSession(
            traderStore.user.getUserId,
            amount,
            pathname,
            addressLineOne,
            addressLineTwo,
            city,
            state.toUpperCase(),
            zip,
            true,
            (id) => {
                uiStore.heapTrack('DepositDebitCardStart', {
                    userId: traderStore.user.getUserId,
                    amount: amount,
                    paymentSessionId: id,
                });
                dialog.setStep('debit-card');
            },
            () => {
                returnToSelectTransferMethod();
                uiStore.pushMessage({
                    text: 'Debit card: Something unexpected happened. Please contact support@kalshi.com',
                    variant: 'neutral',
                });
            },
        );
    };

    const onReviewSavedCardDeposit = (): void => {
        if (!debitCardDepositValidation(amount, true)) {
            return;
        }
        dialog.setFee(fee);
        dialog.setStep('review');
    };

    const getStateErrorText = (): string | undefined => {
        if (!attemptedSubmit) return undefined;
        if (invalidState) return invalidState;
        if (state === '') return 'Enter state';
        return undefined;
    };

    // Add these helper functions before the return statement
    const getAddressErrorText = (): string | undefined => {
        if (!attemptedSubmit) return undefined;
        if (addressErrorMessage) return addressErrorMessage;
        if (addressLineOne === '') return 'Enter street address';
        return undefined;
    };

    const getCityErrorText = (): string | undefined => {
        if (!attemptedSubmit) return undefined;
        if (city === '') return 'Enter city';
        return undefined;
    };

    const getZipErrorText = (): string | undefined => {
        if (!attemptedSubmit) return undefined;
        if (invalidPostalCode) return invalidPostalCode;
        if (zip === '') return 'Enter ZIP code';
        return undefined;
    };

    const defaultFeePercentage = isDebitCardFundMethod
        ? uiStore.featureFlags.debitCardFeeEnabled && !!uiStore.featureFlags.debitCardFee
            ? `${uiStore.featureFlags.debitCardFee}%`
            : '0%'
        : '0%';

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: Spacing(3),
                marginTop: Spacing(2),
            }}
        >
            {invalidMessage && dialog.step === 'billing-address' && (
                <div className={classes.invalidMessage}>
                    <div className={classes.invalidBullet} />
                    <Text.Body.x20 color={Palette.Critical.x10}>{invalidMessage}</Text.Body.x20>
                </div>
            )}
            {dialog.step === 'billing-address' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: Spacing(1.25) }}>
                    <Text.Body.x20 color={Palette.Text.x20}>Address Line 1</Text.Body.x20>
                    <InputGroup>
                        <TextInput
                            type={'text'}
                            value={addressLineOne}
                            onChange={(event): void => {
                                setInvalidMessage('');
                                setAddressErrorMessage('');
                                setAddressLineOne(event.target.value);
                            }}
                            required
                            autoComplete={'section-signup address-line1'}
                            title={'billing-address-line1'}
                            errorText={getAddressErrorText()}
                        />
                    </InputGroup>
                    <Text.Body.x20 color={Palette.Text.x20}>Address line 2 (optional)</Text.Body.x20>
                    <InputGroup>
                        <TextInput
                            type={'text'}
                            value={addressLineTwo}
                            onChange={(event): void => {
                                setInvalidMessage('');
                                setAddressLineTwo(event.target.value);
                            }}
                            autoComplete={'section-signup address-line2'}
                            title={'billing-address-line2'}
                        />
                    </InputGroup>
                    <Text.Body.x20 color={Palette.Text.x20}>City</Text.Body.x20>
                    <InputGroup>
                        <TextInput
                            type={'text'}
                            value={city}
                            onChange={(event): void => {
                                setInvalidMessage('');
                                setCity(event.target.value);
                            }}
                            required
                            autoComplete={'section-signup address-level2'}
                            title={'billing-address-city'}
                            errorText={getCityErrorText()}
                        />
                    </InputGroup>
                    <Text.Body.x20 color={Palette.Text.x20}>State</Text.Body.x20>
                    <InputGroup>
                        <SelectInput
                            id={'billing-state'}
                            name={'billing-state'}
                            title={'billing-state'}
                            className={'border border-fill-x40'}
                            value={state}
                            onChange={(event): void => {
                                setInvalidMessage('');
                                setInvalidState('');
                                setState(event.target.value);
                            }}
                            autoComplete={'section-signup address-level1'}
                            errorText={getStateErrorText()}
                        >
                            <option style={{ display: 'none' }}></option>
                            {states.map((stateOption) => (
                                <option value={stateOption.value} key={stateOption.value}>
                                    {stateOption.label}
                                </option>
                            ))}
                        </SelectInput>
                    </InputGroup>
                    <Text.Body.x20 color={Palette.Text.x20}>Zip code</Text.Body.x20>
                    <InputGroup>
                        <TextInput
                            type={'text'}
                            maxLength={5}
                            value={zip}
                            onChange={(event): void => {
                                setInvalidMessage('');
                                setInvalidPostalCode('');
                                if (/^\d*$/.test(event.target.value)) {
                                    setZip(event.target.value);
                                }
                            }}
                            required
                            autoComplete={'section-signup postal-code'}
                            title={'billing-address-zip'}
                            errorText={getZipErrorText()}
                            inputMode={'numeric'}
                        />
                    </InputGroup>
                </div>
            )}
            {dialog.step !== 'billing-address' && (
                <div className={'pt-8'}>
                    <Padding spacing={1} />
                    <DepositAmountInput
                        formatAmount={formatAmount}
                        stringAmount={stringAmount}
                        onChange={validateAmount}
                        className={clsx(classes.input, isDarkMode && 'text-[#ffffff]')}
                        hasError={!!invalidMessage}
                        autoFocus
                    />
                    <Padding spacing={1.5} />
                    {cardId === 'add' && (
                        <div className={'flex justify-center'}>
                            <p
                                className={
                                    'text-center px-1.5 font-medium mb-0 py-0.5 md:text-base text-xs text-green-x10 bg-green-x20 rounded-x50'
                                }
                            >
                                Debit card only
                            </p>
                        </div>
                    )}
                    <p className={'text-center text-fill-x20 md:text-base text-xs mb-1'}>
                        {
                            // Dynamically show 2% if the fee is 0
                            (isNaN((fee / amount) * 100)
                                ? defaultFeePercentage
                                : `${((fee / amount) * 100).toFixed(0)}%`) + ' processing fee'
                        }
                    </p>
                    {invalidMessage && (
                        <div className={clsx(classes.invalidMessage, 'flex justify-center mb-2')}>
                            <div className={classes.invalidBullet} />
                            <Text.Body.x20 color={Palette.Critical.x10}>{invalidMessage}</Text.Body.x20>
                        </div>
                    )}
                    <Padding spacing={1.5} />
                    <PillGroup stretchToFit pills={pills} className={'w-full justify-between'} fullWidth />
                    {isBankFundMethod && (
                        <div className={'rounded-x30 border border-solid border-fill-x50 mt-3'}>
                            <BankAccountSelect bankId={bankId} setBankId={setBankId} />
                        </div>
                    )}
                    {isDebitCardFundMethod && <DebitCardSelect cardId={cardId} setCardId={setCardId} />}
                </div>
            )}
            {
                <div className={'flex flex-col gap-2'}>
                    {isDebitCardFundMethod && dialog.step !== 'billing-address' && (
                        <SignUpComplianceAgree
                            size={'small'}
                            customText={`By submitting, you acknowledge your consent to debit your card and your agreement to Kalshi's rules and terms.`}
                            customEmphasis={'This payment cannot be cancelled'}
                        />
                    )}
                    <Button
                        variant={'brand'}
                        onClick={(): void => {
                            if (isBankFundMethod) {
                                review();
                            } else if (isDebitCardFundMethod) {
                                if (cardId === 'add') {
                                    userStore.metrics.recordDepositMetric({
                                        step: 'newCardSelected',
                                    });
                                    onContinueAddCard();
                                } else {
                                    onReviewSavedCardDeposit();
                                }
                            }
                        }}
                        text={
                            isBankFundMethod
                                ? 'Review'
                                : isDebitCardFundMethod && dialog.step !== 'billing-address'
                                ? cardId === 'add'
                                    ? 'Add a card'
                                    : 'Add'
                                : 'Continue'
                        }
                        loading={loading}
                        disabled={loading || !stringAmount || !!invalidMessage}
                        fullWidth
                    />
                    {/* <Button
                        variant={'brand'}
                        rank={'secondary'}
                        onClick={returnToSelectTransferMethod}
                        text={'Other transfer methods'}
                        buttonProps={{ type: 'button' }}
                        disabled={loading}
                    /> */}
                    {!!onNotNow && <Button rank={'tertiary'} variant={'brand'} text={'Not now'} onClick={onNotNow} />}
                </div>
            }
        </div>
    );
};

export default observer(DepositPrereviewStep);
